import { useEffect, useState } from 'react';
import { DocAccessType, IAnyObj, ICourse, IDocuments, IModal, IRisk } from '../../../../types';
import { BiometricsModal, PreviousUKVisaModal, TBTestModal, uiTypes, VisaRefusalModal } from 'shared-components';
import { FileUploadModal } from '../../modals/fileUpload/index';
import ATAS from '../../../molecules/documentSection/atas';
import Passport from '../../../molecules/documentSection/passport';
import StudyVisa from '../../../molecules/documentSection/study-visa';
import VisaRefusal from '../../../molecules/documentSection/visa-refusal';
import PreviousStudy from '../../../molecules/documentSection/previous-study';
import EnglishTest from '../../../molecules/documentSection/english-test';
import PostStudyLetter from '../../../molecules/documentSection/post-study-letter';
import ParentalConsent from '../../../molecules/documentSection/parental-consent';
import CAS from '../../../molecules/documentSection/cas';
import TBTest from '../../../molecules/documentSection/tb-test';
import OtherVisa from '../../../molecules/documentSection/other-visa';
import FileRejectModal from '../../modals/fileReject/container';
import FileDeleteModal from '../../modals/fileDelete/container';
import FileRescindRejectionModal from '../../modals/fileRescindRejection';
import CustomRequestDocumentModal from '../../modals/customRequestDoc/container';
import CustomRequestDocument from '../../../molecules/documentSection/customRequest';
import { UserRole } from '../../../../store/auth/types';
import {
  hasPerm,
  isAgentOrAgencyRole,
  isEnrolyStaff,
  isPartnerRole,
  isUniStaffRole,
  P,
} from '../../../../utilities/roles/permissions';
import NoDocuments from '../../../atoms/no-doc-placeholder';
import { hasFundingRisks } from '../../../../utilities/risks';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from '../../../../store';
import { useIsSectionLocked } from '../../../../hooks/useIsSectionLocked';
import { SectionId } from '../../../molecules/studentLeftNav/types';
import LockedSectionWarning from '../../../molecules/locked-section-warning';
import CASBypassWarning from '../../../molecules/casBypassWarning/casBypassWarning';
import EnglishQualification from '../../../molecules/documentSection/english-qualification';
import { useParams } from 'react-router-dom';
import { useCustomNavigate } from '../../../../hooks/useCustomNavigate';
import { getStudentDocs } from '../../../../store/student/actions';
import { Box } from '@chakra-ui/react';
import { DocumentRequestButtons } from '../../../DocumentRequestButtons';
import { SectionGroup } from '../../../Finger/SectionGroup';
import { getDocDetails } from '../../../../utilities/documents';
import { TBTest as ITBTest } from 'types';
import { DocumentViewer } from '../../../organisms/DocumentViewer';

export interface IDispatchProps {
  onUpdateRisks: (source: string, clearedRisks: any) => void;
  onContinue: (
    sponsorType: string,
    sponsorKey: string,
    docType: string | null,
    docKey: string | null,
    uploadedBy: string,
  ) => void;
  openDocumentModal: (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle?: string,
  ) => void;
  openUploadModal: (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    modalTitle: string,
    fileNames: string[],
    metadata?: any,
  ) => void;
  closeModal: () => void;
  onApproveDoc: (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docStatus?: string,
    docPrevStatus?: string,
  ) => void;
  onAcknowledgeDoc: (
    sponsorType: string | null,
    sponsorKey: string | null,
    docType: string | null,
    docKey: string | null,
    docStatus?: string,
    docPrevStatus?: string,
  ) => void;
  onVisaRefusalMetaDataUpdate: (
    sponsorType: string,
    sponsorKey: string,
    docType: string,
    docKey: string,
    metadata?: any,
  ) => void;
  onNoBiometrics: (sponsorType: string, sponsorKey: string, docType: string, docKey: string) => void;
  onTBTestMetaUpdate: (sponsorType: string, docKey: string, metaData: uiTypes.ITBTestMetaData) => void;
}

export interface IProps {
  documents: IDocuments;
  hasRequiredDocs: boolean;
  hasInfoOnlyDocs: boolean;
  hasStaffOnlyDocs: boolean;
  hasAgentOnlyDocs: boolean;
  userRole: UserRole;
  openModal: IModal | null;
  country?: string;
  passportStatus?: string;
  passportExpectedDate?: string;
  passportExpiryDate?: string;
  lastCourse?: string;
  lastCourseOther?: string;
  lastCourseYear?: string;
  hasEnglishTest: boolean;
  englishTestType?: string;
  qualification?: string;
  qualificationOther?: string;
  englishTestNotReqdReason?: string;
  englishTestTypeOther?: string;
  casReceived: boolean;
  courses?: Array<ICourse>;
  risks: IRisk;
  docsRequired: number;
}
type Props = IProps & IDispatchProps;

function DocumentDashboard(props: Props) {
  const [imageArray, setImageArray] = useState<string[] | undefined>();
  const [previewOpen, toggleOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const offer = useSelector(({ student }: ApplicationState) => student.offer);
  const isChecklistSectionLocked = useIsSectionLocked(SectionId.CHECKLIST, offer.status);

  const currentUserRole = useSelector(({ auth }: ApplicationState) => auth.role);

  const isAgent = isAgentOrAgencyRole(currentUserRole);
  const isStaff = isUniStaffRole(currentUserRole);
  const isEnrolyOrPartner = isEnrolyStaff(currentUserRole) || isPartnerRole(currentUserRole);

  const canResume = hasPerm(props.userRole, P.CHECKLIST_EDIT) && !(isAgent && isChecklistSectionLocked);
  const canOpen = hasPerm(props.userRole, P.ALL_DOCUMENTS_PREVIEW);
  const canApprove = hasPerm(props.userRole, P.ALL_DOCUMENTS_APPROVE);
  const canReject = hasPerm(props.userRole, P.ALL_DOCUMENTS_REJECT);
  const canAcknowledge = hasPerm(props.userRole, P.ALL_DOCUMENTS_ACKNOWLEDGE);
  const canRequest = hasPerm(props.userRole, P.CUSTOM_DOCUMENTS_REQUEST);
  const canDelete = hasPerm(props.userRole, P.GENERAL_DOCUMENTS_DELETE);
  const showRisks = hasPerm(props.userRole, P.GENERAL_DOCUMENTS_FLAGS);
  const canCheckOffRisks = hasPerm(props.userRole, P.CHECK_OFF_RISKS);
  const canUploadStaffOnly = hasPerm(props.userRole, P.UPLOAD_STAFF_ONLY_DOCS) && (isStaff || isEnrolyOrPartner);
  const canUploadAgentOnly =
    hasPerm(props.userRole, P.UPLOAD_AGENT_ONLY_DOCS) && (isAgent || isStaff || isEnrolyOrPartner); // Staff and enroly has access of agent docs as well

  const isCasBypass = useSelector(({ student }: ApplicationState) => student.offer.casBypass);
  const isSectionLocked = useIsSectionLocked(SectionId.GENERAL_DOC, offer.status);
  const agentUploadLock = isAgent && isSectionLocked;
  const canUpload = hasPerm(props.userRole, P.GENERAL_DOCUMENTS_UPLOAD) && !agentUploadLock;
  const dispatch = useDispatch();
  const { offerId = '' } = useParams();

  const { navigateTo } = useCustomNavigate();
  const onResume = (url: string) => navigateTo(`${offerId}/${url}`);

  useEffect(() => {
    dispatch(getStudentDocs());
  }, [dispatch]);
  const handleUpdateRisksClick = (target: string, docKey: string, source: string) => {
    let clearedRisks = {};
    const { docsRisk } = props.risks;

    switch (target) {
      case 'parentalConsent':
      case 'birthCertificate':
        const foundDoc = docsRisk[target].find(docRisk => docRisk.id === docKey);
        clearedRisks = {
          docsRisk: {
            [target]: [
              ...(props.risks?.clearedRisks && props.risks?.clearedRisks?.docsRisk
                ? props.risks?.clearedRisks.docsRisk[target]
                : []),
              ...(foundDoc ? [foundDoc] : []),
            ],
          },
        };
        break;
      default:
        break;
    }

    props.onUpdateRisks(`General Document - ${source}`, clearedRisks);
  };

  const checkCheckedOffRisks = (target: string, docKey: string): boolean => {
    if (props.risks.clearedRisks.docsRisk) {
      const { docsRisk } = props.risks.clearedRisks;

      switch (target) {
        case 'parentalConsent':
        case 'birthCertificate':
          return hasFundingRisks(docsRisk[target], docKey);
        default:
          return false;
      }
    }
    return false;
  };

  const renderDocs = (infoOnly = false, userAccess?: DocAccessType) => (
    <>
      <ATAS
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.atas}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onOpenClick={(docKey: string) => {
          setImageArray(props.documents.atas[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docKey: string) =>
          props.openUploadModal(null, null, 'atas', docKey, 'ATAS', props.documents.atas[docKey].fileNames)
        }
        onApproveClick={(docKey: string) => {
          props.onApproveDoc(
            null,
            null,
            'atas',
            docKey,
            props.documents.atas[docKey].status,
            props.documents.atas[docKey].prevStatus,
          );
        }}
        onRejectClick={(docKey: string) => {
          props.openDocumentModal(null, null, 'reject-atas', docKey, 'ATAS Scan');
        }}
        onRescindRejectClick={(docKey: string) => {
          props.openDocumentModal(null, null, 'rescind-reject-atas', docKey, 'ATAS Scan');
        }}
        onDeleteClick={(docKey: string) => {
          props.openDocumentModal(null, null, 'delete-atas', docKey, 'ATAS Scan');
        }}
        onAcknowledgeClick={(docKey: string) => {
          props.onAcknowledgeDoc(
            null,
            null,
            'atas',
            docKey,
            props.documents.atas[docKey].status,
            props.documents.atas[docKey].prevStatus,
          );
        }}
        userRole={props.userRole}
      />
      <StudyVisa
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.studentVisas}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onVisaUploadClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'visa', docKey, 'Study Visa')
        }
        onVisaOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.studentVisas[visaKey].visa![docKey].signedUrls);
          toggleOpen(true);
        }}
        onVisaApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'studentVisas',
            visaKey,
            'visa',
            docKey,
            props.documents.studentVisas[visaKey].visa![docKey].status,
            props.documents.studentVisas[visaKey].visa![docKey].prevStatus,
          )
        }
        onVisaRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'reject-visa', docKey, 'Study Visa Scan')
        }
        onVisaRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'rescind-reject-visa', docKey, 'Study Visa Scan')
        }
        onVisaDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'delete-visa', docKey, 'Study Visa Scan')
        }
        onVisaAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'studentVisas',
            visaKey,
            'visa',
            docKey,
            props.documents.studentVisas[visaKey].visa![docKey].status,
            props.documents.studentVisas[visaKey].visa![docKey].prevStatus,
          )
        }
        onCasUploadClick={(visaKey: string, docKey: string) =>
          props.openUploadModal(
            'studentVisas',
            visaKey,
            'cas',
            docKey,
            'Cas Document',
            props.documents.studentVisas[visaKey].cas![docKey].fileNames,
          )
        }
        onCasOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.studentVisas[visaKey].cas![docKey].signedUrls);
          toggleOpen(true);
        }}
        onCasApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'studentVisas',
            visaKey,
            'cas',
            docKey,
            props.documents.studentVisas[visaKey].cas![docKey].status,
            props.documents.studentVisas[visaKey].cas![docKey].prevStatus,
          )
        }
        onCasRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'reject-cas', docKey, 'CAS Scan')
        }
        onCasRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'rescind-reject-cas', docKey, 'CAS Scan')
        }
        onCasDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'delete-cas', docKey, 'CAS Scan')
        }
        onCasAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'studentVisas',
            visaKey,
            'cas',
            docKey,
            props.documents.studentVisas[visaKey].cas![docKey].status,
            props.documents.studentVisas[visaKey].cas![docKey].prevStatus,
          )
        }
        onBiometricUploadClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'biometric', docKey)
        }
        onBiometricOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.studentVisas[visaKey].biometric![docKey].signedUrls);
          toggleOpen(true);
        }}
        onBiometricApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'studentVisas',
            visaKey,
            'biometric',
            docKey,
            props.documents.studentVisas[visaKey].biometric![docKey].status,
            props.documents.studentVisas[visaKey].biometric![docKey].prevStatus,
          )
        }
        onBiometricRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'reject-biometric', docKey, 'Biometrics Scan')
        }
        onBiometricRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'rescind-reject-biometric', docKey, 'Biometrics Scan')
        }
        onBiometricDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('studentVisas', visaKey, 'delete-biometric', docKey, 'Biometrics Scan')
        }
        onBiometricAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'studentVisas',
            visaKey,
            'biometric',
            docKey,
            props.documents.studentVisas[visaKey].biometric![docKey].status,
            props.documents.studentVisas[visaKey].biometric![docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <OtherVisa
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.otherVisas}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onVisaUploadClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('otherVisas', visaKey, 'visa', docKey)
        }
        onVisaOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.otherVisas[visaKey].visa![docKey].signedUrls);
          toggleOpen(true);
        }}
        onVisaApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'otherVisas',
            visaKey,
            'visa',
            docKey,
            props.documents.otherVisas[visaKey].visa![docKey].status,
            props.documents.otherVisas[visaKey].visa![docKey].prevStatus,
          )
        }
        onVisaRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('otherVisas', visaKey, 'reject-visa', docKey, 'Other Visa Scan')
        }
        onVisaRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('otherVisas', visaKey, 'rescind-reject-visa', docKey, 'Other Visa Scan')
        }
        onVisaDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('otherVisas', visaKey, 'delete-visa', docKey, 'Other Visa Scan')
        }
        onVisaAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'otherVisas',
            visaKey,
            'visa',
            docKey,
            props.documents.otherVisas[visaKey].visa![docKey].status,
            props.documents.otherVisas[visaKey].visa![docKey].prevStatus,
          )
        }
        onBiometricUploadClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('otherVisas', visaKey, 'biometric', docKey)
        }
        onBiometricOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.otherVisas[visaKey].biometric![docKey].signedUrls);
          toggleOpen(true);
        }}
        onBiometricApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'otherVisas',
            visaKey,
            'biometric',
            docKey,
            props.documents.otherVisas[visaKey].biometric![docKey].status,
            props.documents.otherVisas[visaKey].biometric![docKey].prevStatus,
          )
        }
        onBiometricRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('reject-otherVisas', visaKey, 'biometric', docKey, 'Biometrics Scan')
        }
        onBiometricRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('rescind-reject-otherVisas', visaKey, 'biometric', docKey, 'Biometrics Scan')
        }
        onBiometricDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('delete-otherVisas', visaKey, 'biometric', docKey, 'Biometrics Scan')
        }
        onBiometricAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'otherVisas',
            visaKey,
            'biometric',
            docKey,
            props.documents.otherVisas[visaKey].biometric![docKey].status,
            props.documents.otherVisas[visaKey].biometric![docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <VisaRefusal
        key="visa-refusal"
        isNonUKVisaRefusal={false}
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        showRisks={showRisks}
        onResume={onResume}
        onUploadClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('visaRefusals', visaKey, null, docKey)
        }
        onOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.visaRefusals[visaKey][docKey].signedUrls);
          toggleOpen(true);
        }}
        onApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'visaRefusals',
            visaKey,
            null,
            docKey,
            props.documents.visaRefusals[visaKey][docKey].status,
            props.documents.visaRefusals[visaKey][docKey].prevStatus,
          )
        }
        onRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('visaRefusals', visaKey, 'reject-null', docKey, 'Visa Refusal Scan')
        }
        onRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('visaRefusals', visaKey, 'rescind-reject-null', docKey, 'Visa Refusal Scan')
        }
        onDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('visaRefusals', visaKey, 'delete-null', docKey, 'Visa Refusal Scan')
        }
        onAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'visaRefusals',
            visaKey,
            null,
            docKey,
            props.documents.visaRefusals[visaKey][docKey].status,
            props.documents.visaRefusals[visaKey][docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <VisaRefusal
        key="non-uk-visa-refusal"
        isNonUKVisaRefusal={true}
        infoOnly={infoOnly}
        userAccess={userAccess}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        showRisks={showRisks}
        documents={props.documents}
        onResume={onResume}
        onUploadClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('nonUkVisaRefusals', visaKey, null, docKey)
        }
        onOpenClick={(visaKey: string, docKey: string) => {
          setImageArray(props.documents.nonUkVisaRefusals[visaKey][docKey].signedUrls);
          toggleOpen(true);
        }}
        onApproveClick={(visaKey: string, docKey: string) =>
          props.onApproveDoc(
            'nonUkVisaRefusals',
            visaKey,
            null,
            docKey,
            props.documents.nonUkVisaRefusals[visaKey][docKey].status,
            props.documents.nonUkVisaRefusals[visaKey][docKey].prevStatus,
          )
        }
        onRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('nonUkVisaRefusals', visaKey, 'reject-null', docKey, 'Non-UK Visa Refusal Scan')
        }
        onRescindRejectClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal(
            'nonUkVisaRefusals',
            visaKey,
            'rescind-reject-null',
            docKey,
            'Non-UK Visa Refusal Scan',
          )
        }
        onDeleteClick={(visaKey: string, docKey: string) =>
          props.openDocumentModal('nonUkVisaRefusals', visaKey, 'delete-null', docKey, 'Non-UK Visa Refusal Scan')
        }
        onAcknowledgeClick={(visaKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'nonUkVisaRefusals',
            visaKey,
            null,
            docKey,
            props.documents.nonUkVisaRefusals[visaKey][docKey].status,
            props.documents.nonUkVisaRefusals[visaKey][docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <Passport
        infoOnly={infoOnly}
        userAccess={userAccess}
        passportStatus={props.passportStatus!}
        passportExpiryDate={props.passportExpiryDate!}
        passportExpectedDate={props.passportExpectedDate!}
        courses={props.courses ?? []}
        documents={props.documents.passport}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onOpenClick={(docType: string, docKey: string) => {
          setImageArray(props.documents.passport[docType][docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docType: string, docKey: string) =>
          props.openUploadModal(
            'passport',
            null,
            docType,
            docKey,
            'Passport',
            props.documents.passport[docType][docKey].fileNames,
          )
        }
        onApproveClick={(docType: string, docKey: string) =>
          props.onApproveDoc(
            'passport',
            null,
            docType,
            docKey,
            props.documents.passport[docType][docKey].status,
            props.documents.passport[docType][docKey].prevStatus,
          )
        }
        onRejectClick={(docType: string, docKey: string) => {
          props.openDocumentModal('passport', null, `reject-${docType}`, docKey, 'Passport Scan');
        }}
        onRescindRejectClick={(docType: string, docKey: string) => {
          props.openDocumentModal('passport', null, `rescind-reject-${docType}`, docKey, 'Passport Scan');
        }}
        onDeleteClick={(docType: string, docKey: string) => {
          props.openDocumentModal('passport', null, `delete-${docType}`, docKey, 'Passport Scan');
        }}
        onAcknowledgeClick={(docType: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'passport',
            null,
            docType,
            docKey,
            props.documents.passport[docType][docKey].status,
            props.documents.passport[docType][docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <PreviousStudy
        infoOnly={infoOnly}
        userAccess={userAccess}
        courseName={props.lastCourse !== 'Other' ? props.lastCourse! : props.lastCourseOther!}
        courseYear={props.lastCourseYear!}
        documents={props.documents}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onAcademicCertificateOpenClick={(docKey: string) => {
          setImageArray(props.documents.academicCertificate[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onAcademicCertificateUploadClick={(docKey: string) =>
          props.openUploadModal(
            null,
            null,
            'academicCertificate',
            docKey,
            'Academic Certificate',
            props.documents.academicCertificate[docKey].fileNames,
          )
        }
        onAcademicCertificateApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'academicCertificate',
            docKey,
            props.documents.academicCertificate[docKey].status,
            props.documents.academicCertificate[docKey].prevStatus,
          )
        }
        onAcademicCertificateRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'reject-academicCertificate', docKey, 'Academic Certificate')
        }
        onAcademicCertificateRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'rescind-reject-academicCertificate', docKey, 'Academic Certificate')
        }
        onAcademicCertificateDeleteClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'delete-academicCertificate', docKey, 'Academic Certificate')
        }
        onAcademicCertificateAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'academicCertificate',
            docKey,
            props.documents.academicCertificate[docKey].status,
            props.documents.academicCertificate[docKey].prevStatus,
          )
        }
        onAcademicTranscriptOpenClick={(docKey: string) => {
          setImageArray(props.documents.academicTranscript[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onAcademicTranscriptUploadClick={(docKey: string) =>
          props.openUploadModal(
            null,
            null,
            'academicTranscript',
            docKey,
            'Academic Transcript',
            props.documents.academicTranscript[docKey].fileNames,
          )
        }
        onAcademicTranscriptApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'academicTranscript',
            docKey,
            props.documents.academicTranscript[docKey].status,
            props.documents.academicTranscript[docKey].prevStatus,
          )
        }
        onAcademicTranscriptRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'reject-academicTranscript', docKey, 'Academic Transcript')
        }
        onAcademicTranscriptRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'rescind-reject-academicTranscript', docKey, 'Academic Transcript')
        }
        onAcademicTranscriptDeleteClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'delete-academicTranscript', docKey, 'Academic Transcript')
        }
        onAcademicTranscriptAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'academicTranscript',
            docKey,
            props.documents.academicTranscript[docKey].status,
            props.documents.academicTranscript[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <EnglishTest
        infoOnly={infoOnly}
        userAccess={userAccess}
        englishTestDone={props.hasEnglishTest}
        englishTestType={props.englishTestType !== 'Other' ? props.englishTestType : props.englishTestTypeOther}
        englishTestNotReqdReason={props.englishTestNotReqdReason}
        documents={props.documents.englishTest}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onOpenClick={(docKey: string) => {
          setImageArray(props.documents.englishTest[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docKey: string) =>
          props.openUploadModal(
            null,
            null,
            'englishTest',
            docKey,
            'English Test',
            props.documents.englishTest[docKey].fileNames,
          )
        }
        onApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'englishTest',
            docKey,
            props.documents.englishTest[docKey].status,
            props.documents.englishTest[docKey].prevStatus,
          )
        }
        onRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'reject-englishTest', docKey, 'English Test Scan')
        }
        onRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'rescind-reject-englishTest', docKey, 'English Test Scan')
        }
        onDeleteClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'delete-englishTest', docKey, 'English Test Scan')
        }
        onAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'englishTest',
            docKey,
            props.documents.englishTest[docKey].status,
            props.documents.englishTest[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />

      <EnglishQualification
        infoOnly={infoOnly}
        userAccess={userAccess}
        qualification={props.qualification}
        qualificationOther={props.qualificationOther}
        documents={props.documents.englishQualification}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onOpenClick={(docKey: string) => {
          setImageArray(props.documents.englishQualification[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docKey: string) =>
          props.openUploadModal(
            null,
            null,
            'englishQualification',
            docKey,
            'English Qualification',
            props.documents.englishQualification[docKey].fileNames,
          )
        }
        onApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'englishQualification',
            docKey,
            props.documents.englishQualification[docKey].status,
            props.documents.englishQualification[docKey].prevStatus,
          )
        }
        onRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'reject-englishQualification', docKey, 'English Qualification Scan')
        }
        onRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(
            null,
            null,
            'rescind-reject-englishQualification',
            docKey,
            'English Qualification Scan',
          )
        }
        onDeleteClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'delete-englishQualification', docKey, 'English Qualification Scan')
        }
        onAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'englishQualification',
            docKey,
            props.documents.englishQualification[docKey].status,
            props.documents.englishQualification[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />

      <ParentalConsent
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.parentalConsent}
        birthCertificate={props.documents.birthCertificate}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        showRisks={showRisks}
        docRisk={props.risks.docsRisk}
        canCheckOff={canCheckOffRisks}
        isCheckedOff={(docType: string, docKey: string) => {
          return checkCheckedOffRisks(docType, docKey);
        }}
        onCheckOffRisk={(docType: string, docKey: string, docTitle: string) => {
          handleUpdateRisksClick(docType, docKey, docTitle);
        }}
        onResume={onResume}
        onOpenClick={(sponsorType: string, docType: string | null, docKey: string) => {
          setImageArray(
            sponsorType === 'birthCertificate'
              ? props.documents.birthCertificate[docType!][docKey].signedUrls
              : props.documents.parentalConsent[docKey].signedUrls!,
          );
          toggleOpen(true);
        }}
        onUploadClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) => {
          props.openUploadModal(
            sponsorType,
            null,
            docType,
            docKey,
            modalTitle,
            sponsorType === 'birthCertificate'
              ? props.documents.birthCertificate[docType!][docKey].fileNames
              : props.documents.parentalConsent[docKey].fileNames,
          );
        }}
        onApproveClick={(sponsorType: string, docType: string | null, docKey: string) =>
          props.onApproveDoc(
            sponsorType,
            null,
            docType,
            docKey,
            sponsorType === 'birthCertificate'
              ? props.documents.birthCertificate[docType!][docKey].status
              : props.documents.parentalConsent[docKey].status,
            sponsorType === 'birthCertificate'
              ? props.documents.birthCertificate[docType!][docKey].prevStatus
              : props.documents.parentalConsent[docKey].prevStatus,
          )
        }
        onRejectClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
          props.openDocumentModal(sponsorType, null, `reject-${docType}`, docKey, modalTitle)
        }
        onRescindRejectClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
          props.openDocumentModal(sponsorType, null, `rescind-reject-${docType}`, docKey, modalTitle)
        }
        onDeleteClick={(sponsorType: string, docType: string | null, docKey: string, modalTitle: string) =>
          props.openDocumentModal(sponsorType, null, `delete-${docType}`, docKey, modalTitle)
        }
        onAcknowledgeClick={(sponsorType: string, docType: string | null, docKey: string) =>
          props.onAcknowledgeDoc(
            sponsorType,
            null,
            docType,
            docKey,
            sponsorType === 'birthCertificate'
              ? props.documents.birthCertificate[docType!][docKey].status
              : props.documents.parentalConsent[docKey].status,
            sponsorType === 'birthCertificate'
              ? props.documents.birthCertificate[docType!][docKey].prevStatus
              : props.documents.parentalConsent[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <TBTest
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.tbTest}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onOpenClick={(docKey: string) => {
          setImageArray(props.documents.tbTest[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docKey: string) => props.openDocumentModal(null, null, 'tbTest', docKey)}
        onApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'tbTest',
            docKey,
            props.documents.tbTest[docKey].status,
            props.documents.tbTest[docKey].prevStatus,
          )
        }
        onRejectClick={(docKey: string) => props.openDocumentModal(null, null, 'reject-tbTest', docKey, 'TB Test Scan')}
        onRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'rescind-reject-tbTest', docKey, 'TB Test Scan')
        }
        onDeleteClick={(docKey: string) => props.openDocumentModal(null, null, 'delete-tbTest', docKey, 'TB Test Scan')}
        onAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'tbTest',
            docKey,
            props.documents.tbTest[docKey].status,
            props.documents.tbTest[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <CAS
        infoOnly={infoOnly}
        userAccess={userAccess}
        casReceived={props.casReceived}
        documents={props.documents.cas}
        canResume={canResume}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onResume={onResume}
        onOpenClick={(docKey: string) => {
          setImageArray(props.documents.cas[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docKey: string) =>
          props.openUploadModal(null, null, 'cas', docKey, 'CAS Document', props.documents.cas[docKey].fileNames)
        }
        onApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'cas',
            docKey,
            props.documents.cas[docKey].status,
            props.documents.cas[docKey].prevStatus,
          )
        }
        onRejectClick={(docKey: string) => props.openDocumentModal(null, null, 'reject-cas', docKey, 'CAS Document')}
        onRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'rescind-reject-cas', docKey, 'CAS Document')
        }
        onDeleteClick={(docKey: string) => props.openDocumentModal(null, null, 'delete-cas', docKey, 'CAS Document')}
        onAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'cas',
            docKey,
            props.documents.cas[docKey].status,
            props.documents.cas[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <PostStudyLetter
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.postStudyLetter}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onOpenClick={(docKey: string) => {
          setImageArray(props.documents.postStudyLetter[docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(docKey: string) =>
          props.openUploadModal(
            null,
            null,
            'postStudyLetter',
            docKey,
            'Personal Post-Study Letter',
            props.documents.postStudyLetter[docKey].fileNames,
          )
        }
        onApproveClick={(docKey: string) =>
          props.onApproveDoc(
            null,
            null,
            'postStudyLetter',
            docKey,
            props.documents.postStudyLetter[docKey].status,
            props.documents.postStudyLetter[docKey].prevStatus,
          )
        }
        onRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'reject-postStudyLetter', docKey, 'Personal Post-Study Letter')
        }
        onRescindRejectClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'rescind-reject-postStudyLetter', docKey, 'Personal Post-Study Letter')
        }
        onDeleteClick={(docKey: string) =>
          props.openDocumentModal(null, null, 'delete-postStudyLetter', docKey, 'Personal Post-Study Letter')
        }
        onAcknowledgeClick={(docKey: string) =>
          props.onAcknowledgeDoc(
            null,
            null,
            'postStudyLetter',
            docKey,
            props.documents.postStudyLetter[docKey].status,
            props.documents.postStudyLetter[docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
      <CustomRequestDocument
        infoOnly={infoOnly}
        userAccess={userAccess}
        documents={props.documents.customRequest}
        canUpload={canUpload}
        canOpen={canOpen}
        canApprove={canApprove}
        canReject={canReject}
        isDeleting={isDeleting}
        canAcknowledge={canAcknowledge}
        onOpenClick={(customReqKey: string, docKey: string) => {
          setImageArray(props.documents.customRequest[customReqKey][docKey].signedUrls!);
          toggleOpen(true);
        }}
        onUploadClick={(customReqKey: string, docKey: string) =>
          props.openUploadModal(
            'customRequest',
            customReqKey,
            null,
            docKey,
            'Custom Request',
            props.documents.customRequest[customReqKey][docKey].fileNames,
          )
        }
        onApproveClick={(customReqKey: string, docKey: string) =>
          props.onApproveDoc(
            'customRequest',
            customReqKey,
            null,
            docKey,
            props.documents.customRequest[customReqKey][docKey].status,
            props.documents.customRequest[customReqKey][docKey].prevStatus,
          )
        }
        onRejectClick={(customReqKey: string, docKey: string) => {
          props.openDocumentModal('customRequest', customReqKey, 'reject-null', docKey, 'Custom Document');
        }}
        onRescindRejectClick={(customReqKey: string, docKey: string) => {
          props.openDocumentModal('customRequest', customReqKey, 'rescind-reject-null', docKey, 'Custom Document');
        }}
        onDeleteClick={(customReqKey: string, docKey: string) => {
          props.openDocumentModal('customRequest', customReqKey, 'delete-null', docKey, 'Custom Document');
        }}
        onAcknowledgeClick={(customReqKey: string, docKey: string) =>
          props.onAcknowledgeDoc(
            'customRequest',
            customReqKey,
            null,
            docKey,
            props.documents.customRequest[customReqKey][docKey].status,
            props.documents.customRequest[customReqKey][docKey].prevStatus,
          )
        }
        userRole={props.userRole}
      />
    </>
  );

  const getUserRole = (role: UserRole) => {
    return [UserRole.Uni, UserRole.UniAdmin, UserRole.UniLite, UserRole.UniSecure].includes(role)
      ? 'University'
      : role === UserRole.Admin
        ? 'Admin'
        : role === UserRole.Agent
          ? 'Agent'
          : role === UserRole.Enroly
            ? 'Enroly'
            : role === UserRole.AgencyAdmin
              ? 'AgencyAdmin'
              : '';
  };

  const { openModal } = props;
  if (openModal?.isUpload) {
    return <FileUploadModal {...openModal} />;
  }

  if (openModal?.docType?.startsWith('delete-')) {
    return <FileDeleteModal onDelete={() => setIsDeleting(false)} />;
  }
  if (openModal?.docType?.startsWith('reject-')) {
    return <FileRejectModal />;
  }
  if (openModal?.docType?.startsWith('rescind-reject-')) {
    return <FileRescindRejectionModal />;
  }

  switch (openModal?.sponsorType) {
    case 'customRequestModal':
      return <CustomRequestDocumentModal />;
    case 'nonUkVisaRefusals':
    case 'visaRefusals': {
      const doc = getDocDetails({
        docs: props.documents,
        sponsorType: openModal.sponsorType,
        sponsorKey: openModal.sponsorKey,
        docType: openModal.docType,
        docKey: openModal.docKey,
      }) as IAnyObj;
      const modalTitle = openModal.docType === 'nonUkVisaRefusals' ? 'Non-UK Visa Refusal' : 'Visa Refusal';
      return (
        <VisaRefusalModal
          docKey={openModal.docKey!}
          infoOnly={!!openModal.isInfoOnly}
          docStatus={doc.status}
          onAcknowledgeToggle={(status: string) =>
            props.onAcknowledgeDoc(
              openModal?.sponsorType!,
              openModal?.sponsorKey!,
              openModal?.docType!,
              openModal?.docKey!,
              status,
            )
          }
          complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
          status={doc.status}
          isNonUkVisaRefusal={openModal.docType === 'nonUkVisaRefusals'}
          hasVisaRefusalDoc={doc.hasVisaRefusalDoc}
          hasNoVisaRefusalDocReason={doc.hasNoVisaRefusalDocReason}
          refusalDate={doc.refusalDate}
          fileNames={doc.fileNames}
          onContinue={() => {
            props.onContinue(
              openModal?.sponsorType!,
              openModal?.sponsorKey!,
              openModal?.docType!,
              openModal?.docKey!,
              getUserRole(props.userRole ?? UserRole.Unknown),
            );
          }}
          onUpload={(metadata?: any) =>
            props.openUploadModal(
              openModal?.sponsorType!,
              openModal?.sponsorKey!,
              openModal?.docType!,
              openModal?.docKey!,
              modalTitle,
              doc.fileNames!,
              metadata,
            )
          }
          onClose={props.closeModal}
          onVisaRefusalMetaDataUpdate={(metadata?: any) =>
            props.onVisaRefusalMetaDataUpdate(
              openModal?.sponsorType!,
              openModal?.sponsorKey!,
              openModal?.docType!,
              openModal?.docKey!,
              metadata,
            )
          }
        />
      );
    }
    case 'studentVisas': {
      const doc = getDocDetails({
        docs: props.documents,
        sponsorType: openModal.sponsorType,
        sponsorKey: openModal.sponsorKey,
        docType: openModal.docType,
        docKey: openModal.docKey,
      }) as IAnyObj;

      switch (openModal.docType) {
        case 'visa': {
          return (
            <PreviousUKVisaModal
              isStudentVisa={true}
              visaType={doc.visaType}
              year={doc.year}
              docKey={openModal.docKey!}
              infoOnly={!!openModal.isInfoOnly}
              docStatus={doc.status}
              status={doc.status}
              onAcknowledgeToggle={(status: string) =>
                props.onAcknowledgeDoc(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  status,
                )
              }
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              fileNames={doc.fileNames}
              onContinue={() => {
                props.onContinue(
                  openModal?.sponsorType!,
                  openModal?.sponsorKey!,
                  openModal?.docType!,
                  openModal?.docKey!,
                  getUserRole(props.userRole ?? UserRole.Unknown),
                );
              }}
              onUpload={(metadata?: any) =>
                props.openUploadModal(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  'Previous Student Visa',
                  doc.fileNames!,
                  metadata,
                )
              }
              onClose={props.closeModal}
              startDate={doc.startDate}
              endDate={doc.endDate}
            />
          );
        }
        case 'biometric': {
          return (
            <BiometricsModal
              docKey={openModal.docKey!}
              infoOnly={!!openModal.isInfoOnly}
              docStatus={doc.status}
              onAcknowledgeToggle={(status: string) =>
                props.onAcknowledgeDoc(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  status,
                )
              }
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              fileNames={doc.fileNames}
              hasDocument={doc.hasDocument}
              onContinue={() => {
                props.onContinue(
                  openModal?.sponsorType!,
                  openModal?.sponsorKey!,
                  openModal?.docType!,
                  openModal?.docKey!,
                  getUserRole(props.userRole ?? UserRole.Unknown),
                );
              }}
              onUpload={(metadata?: any) =>
                props.openUploadModal(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  'Previous Biometric Residence Permit',
                  doc.fileNames!,
                  metadata,
                )
              }
              onClose={props.closeModal}
              onNoBiometrics={() =>
                props.onNoBiometrics(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                )
              }
            />
          );
        }
        default:
          return null;
      }
    }
    case 'otherVisas': {
      const doc = getDocDetails({
        docs: props.documents,
        sponsorType: openModal.sponsorType,
        sponsorKey: openModal.sponsorKey,
        docType: openModal.docType,
        docKey: openModal.docKey,
      }) as IAnyObj;

      switch (openModal.docType) {
        case 'visa': {
          return (
            <PreviousUKVisaModal
              isStudentVisa={false}
              visaType={doc.visaType}
              year={doc.year}
              docKey={openModal.docKey!}
              infoOnly={!!openModal.isInfoOnly}
              docStatus={doc.status}
              status={doc.status}
              onAcknowledgeToggle={(status: string) =>
                props.onAcknowledgeDoc(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  status,
                )
              }
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              fileNames={doc.fileNames}
              onContinue={() => {
                props.onContinue(
                  openModal?.sponsorType!,
                  openModal?.sponsorKey!,
                  openModal?.docType!,
                  openModal?.docKey!,
                  getUserRole(props.userRole ?? UserRole.Unknown),
                );
              }}
              onUpload={(metadata?: any) =>
                props.openUploadModal(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  'Other Visa',
                  doc.fileNames!,
                  metadata,
                )
              }
              onClose={props.closeModal}
              startDate={doc.startDate}
              endDate={doc.endDate}
            />
          );
        }
        case 'biometric': {
          return (
            <BiometricsModal
              docKey={openModal.docKey!}
              infoOnly={!!openModal.isInfoOnly}
              docStatus={doc.status}
              onAcknowledgeToggle={(status: string) =>
                props.onAcknowledgeDoc(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  status,
                )
              }
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              fileNames={doc.fileNames}
              hasDocument={doc.hasDocument}
              onContinue={() => {
                props.onContinue(
                  openModal?.sponsorType!,
                  openModal?.sponsorKey!,
                  openModal?.docType!,
                  openModal?.docKey!,
                  getUserRole(props.userRole ?? UserRole.Unknown),
                );
              }}
              onUpload={(metadata?: any) =>
                props.openUploadModal(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  'Previous Biometric Residence Permit',
                  doc.fileNames!,
                  metadata,
                )
              }
              onClose={props.closeModal}
              onNoBiometrics={() =>
                props.onNoBiometrics(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                )
              }
            />
          );
        }
        default:
          return null;
      }
    }
    default:
      switch (openModal?.docType) {
        case 'tbTest': {
          const doc = getDocDetails({
            docs: props.documents,
            sponsorType: openModal.sponsorType,
            sponsorKey: openModal.sponsorKey,
            docType: openModal.docType,
            docKey: openModal.docKey,
          }) as ITBTest;

          return (
            <TBTestModal
              infoOnly={!!openModal.isInfoOnly}
              docStatus={doc.status}
              onAcknowledgeToggle={(status: string) =>
                props.onAcknowledgeDoc(
                  openModal.sponsorType!,
                  openModal.sponsorKey!,
                  openModal.docType!,
                  openModal.docKey!,
                  status,
                )
              }
              document={doc}
              docKey={openModal.docKey!}
              complete={['UPLOADED_OK', 'APPROVED'].includes(doc.status)}
              fileNames={doc.fileNames}
              onContinue={() => {
                props.onContinue(
                  openModal?.sponsorType!,
                  openModal?.sponsorKey!,
                  openModal?.docType!,
                  openModal?.docKey!,
                  getUserRole(props.userRole ?? UserRole.Unknown),
                );
              }}
              onUpload={props.openUploadModal}
              onClose={props.closeModal}
              onTBTestMetaUpdate={(metaData: uiTypes.ITBTestMetaData) =>
                props.onTBTestMetaUpdate(openModal.sponsorType!, openModal.docKey!, metaData)
              }
            />
          );
        }
      }
      return (
        <>
          <Box>
            {props.hasRequiredDocs || props.hasStaffOnlyDocs || props.hasAgentOnlyDocs || props.hasInfoOnlyDocs ? (
              <>
                {isSectionLocked && <LockedSectionWarning />}

                {props.hasRequiredDocs ? renderDocs() : null}

                {props.hasStaffOnlyDocs && canUploadStaffOnly ? (
                  <SectionGroup
                    heading="The below documents can only be uploaded by a staff member"
                    description="The student has not been asked to provide these documents."
                    data-selector={'staff-only-docs-section'}
                  >
                    {renderDocs(false, DocAccessType.STAFF_ONLY)}
                  </SectionGroup>
                ) : null}

                {props.hasAgentOnlyDocs && canUploadAgentOnly ? (
                  <SectionGroup
                    heading="The below documents can only be uploaded by an agent"
                    description="The student has not been asked to provide these documents."
                    data-selector={'agent-only-docs-section'}
                  >
                    {renderDocs(false, DocAccessType.AGENT_ONLY)}
                  </SectionGroup>
                ) : null}

                {props.hasInfoOnlyDocs ? (
                  <SectionGroup
                    heading="The below documents may be spot checked during the visa application process"
                    description="No upload is required, the student has been asked to read and confirm they understand. If you have
                    notified the student personally, click the Acknowledge button."
                    data-selector={'info-only-docs-section'}
                  >
                    {renderDocs(true)}
                  </SectionGroup>
                ) : null}
              </>
            ) : (
              <>
                {(props.hasStaffOnlyDocs && canUploadStaffOnly) || (props.hasAgentOnlyDocs && canUploadAgentOnly) ? (
                  <>
                    {props.hasStaffOnlyDocs && canUploadStaffOnly ? (
                      <SectionGroup
                        heading="The below documents can only be uploaded by a staff member"
                        description="The student has not been asked to provide these documents."
                        data-selector={'staff-only-docs-section'}
                      >
                        {renderDocs(false, DocAccessType.STAFF_ONLY)}
                      </SectionGroup>
                    ) : null}

                    {props.hasAgentOnlyDocs && canUploadAgentOnly ? (
                      <SectionGroup
                        heading="The below documents can only be uploaded by an agent"
                        description="The student has not been asked to provide these documents."
                        data-selector={'agent-only-docs-section'}
                      >
                        {renderDocs(false, DocAccessType.AGENT_ONLY)}
                      </SectionGroup>
                    ) : null}
                  </>
                ) : (
                  <>
                    {isCasBypass && <CASBypassWarning />}

                    <NoDocuments />
                  </>
                )}
              </>
            )}

            <DocumentRequestButtons
              canRequest={canRequest}
              canDelete={canDelete}
              isDeleting={isDeleting}
              onRequestClick={() =>
                props.openDocumentModal('customRequestModal', null, null, null, 'Custom Document Request')
              }
              onDeleteClick={() => setIsDeleting(!isDeleting)}
            />
          </Box>

          {previewOpen && imageArray && imageArray.length > 0 && (
            <DocumentViewer documents={imageArray} onClose={() => toggleOpen(false)} />
          )}
        </>
      );
  }
}

export default DocumentDashboard;
